import { useContext, useEffect } from 'react'
import swal from 'sweetalert2'

import {
  Button, Form, ModalContext, SidebarModal,
} from '@domain-group/suit-theme'

import { digObject } from '@domain-group/javascript-utils'

import { useSetState } from '@domain-group/react-hooks'

import type { HandleCallbackActionParams } from '@functions/handleCallbackAction'
import EntitySelector from '@components/EntitySelector'

import useProcessingFee from '@hooks/useProcessingFee'

const MODAL_KEY = 'CreateOrEditProcessingFeeModal'

const getEntityString = (string: string) => {
  if (string === 'Agency') return 'agencies'
  if (string === 'Brand') return 'brands'
  if (string === 'Provider') return 'providers'
  return ''
}

type ModalCallbacks = {
  closeModal: () => void,
  bulkDeleteProcessingFees: (payload: HandleCallbackActionParams) => Promise<object>,
}

type ModalProps = {
  callbacks: ModalCallbacks,
  showModal: boolean,
}

type UserProcessingFeePayload = ReturnType<typeof useProcessingFee>

type ConfirmDeleteParams = {
  callbacks: ModalCallbacks,
  debtor: {
    debtorId?: number,
    debtorType: string,
  },
  deleteFn: UserProcessingFeePayload['callbacks']['bulkDeleteProcessingFees'],
}

const confirmDelete = (params: ConfirmDeleteParams) => {
  const { callbacks, debtor, deleteFn } = params
  const { closeModal, bulkDeleteProcessingFees } = callbacks

  const deleteProcessingFeePayload = {
    callbacks: {
      action: deleteFn,
      afterAction: closeModal,
    },
    entityParams: debtor,
    toastText: 'Processing Fees Deleted',
  }

  swal
    .fire({
      confirmButtonColor: '#DD6B55',
      confirmButtonText: 'Yes, delete it.',
      icon: 'warning',
      showCancelButton: true,
      text: 'Some Processing Fees for this debtor may not be currently shown. Are you sure?',
      title: 'Delete Processing Fees?',
    })
    .then(({ value }) => {
      if (value){
        // @ts-expect-error TS2345
        bulkDeleteProcessingFees(deleteProcessingFeePayload)
      }
    })
}

type DefaultState = {
  debtorId?: number | null,
  debtorType: string,
}

const defaultState: DefaultState = {
  debtorId: undefined,
  debtorType: '',
}

const CreateOrEditProcessingFeeModal = (props: ModalProps) => {
  const { callbacks, showModal } = props

  const [state, setState] = useSetState(defaultState)
  const { debtorId, debtorType } = state

  const modalContext = useContext(ModalContext)
  const { modalData } = modalContext

  const modalPayload = digObject(modalData, MODAL_KEY, {})
  const { debtor } = modalPayload

  const processingFeePayload = useProcessingFee()
  const {
    callbacks: {
      bulkDeleteProcessingFees: deleteFn,
    },
    deleting,
  } = processingFeePayload

  useEffect(() => {
    setState({ debtorId: debtor?.debtorId, debtorType: debtor?.debtorType })
  }, [debtor])

  const saveEnabled = !!debtorId && !!debtorId

  return (
    <SidebarModal callbacks={callbacks} clickSafeZone modalKey={MODAL_KEY} showModal={showModal}>
      <SidebarModal.Header callbacks={callbacks} title="Bulk Delete" titleSecondLine="Processing Fees" />

      <SidebarModal.Content>
        <Form>
          <Form.Field label="* Debtor Type">
            <select
              onChange={e => setState({ debtorType: e.target.value })}
              value={debtorType}
            >
              <option value="">Please Select...</option>
              <option value="Agency">Agency</option>
              <option value="Brand">Brand</option>
              <option value="Provider">Provider</option>
            </select>
          </Form.Field>

          {debtorType && (
            <EntitySelector
              callbacks={{ selectItem: entity => setState({ debtorId: entity?.id }) }}
              entityKey={getEntityString(debtorType)}
              label={debtorType}
              marginTop="large"
              // @ts-expect-error TS2322
              selectedItemId={debtorId}
            />
          )}

        </Form>
      </SidebarModal.Content>

      <SidebarModal.Footer>
        <Button
          buttonStyle="primaryCreate"
          disabled={!saveEnabled}
          loading={deleting}
          // @ts-expect-error TS2322
          onClick={() => confirmDelete({ callbacks, debtor: state, deleteFn })}
          size="large"
        >
          {saveEnabled ? 'Delete Processing Fees' : 'Complete Fields'}
        </Button>
      </SidebarModal.Footer>
    </SidebarModal>
  )
}

const LazyLoadedModal = (props: ModalProps) => (
  <SidebarModal.RenderController {...props}>
    <CreateOrEditProcessingFeeModal {...props} />
  </SidebarModal.RenderController>
)

export default LazyLoadedModal
